// Вкладка «Наборы» раздела «Учётки и доступ» (не самостоятельный экран: шапку с
// <h1> и маршрут держит pages/AdminUsers.tsx — потому файл в components/, а не в
// pages/, и общие проверки вида экранов на него намеренно не распространяются).
// Здесь владелец правит профили — составы наборов разделов для ЛЮБОГО рабочего
// места (инженер, координатор, коммерция, бухгалтерия, руководство, админ…) —
// и привязку отделов, сам, без программиста и выкладки. Хранится это в
// nabory.json рядом с users.json (BFF), и файл ПЕРЕКРЫВАЕТ кодовые наборы
// rights.НАБОРЫ по совпадающим кодам.
//
// ВИД: слева список профилей (и кнопка «новый»), справа — редактор выбранного:
// каталог ВСЕХ разделов с чекбоксами по группам реестра, переключатель «видит
// всё (не сужать)» и учётки, которым профиль назначен.
//
// ГЛАВНОЕ ПРО МОДЕЛЬ, и экран это говорит вслух: набор только ПРЯЧЕТ разделы
// (состав меню и стартовой), права на данные даёт РОЛЬ. Раздел вне роли не
// появится, какой бы набор ни был назначен, — сервер пересекает, а не объединяет.
// Поэтому владельческих разделов (касса, овертаймы, эта же админка) в чекбоксах
// нет вовсе: они выдаются поимённо ПОСЛЕ пересечения и набором не отрезаются.
// «Видит всё (не сужать)» — тоже не расширение: он лишь не режет то, что дала роль.

import { useState } from 'react'

import type { Набор, РазделДляНабора } from '../api/nabory'
import {
  useНаборы, useПривязкаОтдела, useСохранениеНабора, useУдалениеНабора,
} from '../api/nabory'
import { QueryState } from './QueryState'

/** Русские подписи групп реестра rights.РАЗДЕЛЫ. Коды групп в реестре и так
 * почти все по-русски, но со строчной — на экране им положено имя, а не код.
 * Незнакомая группа не роняет экран: подпись — код с заглавной. */
const ИМЯ_ГРУППЫ: Record<string, string> = {
  crm: 'CRM',
  'деньги': 'Деньги',
  'документы': 'Документы',
  'аналитика': 'Аналитика',
  'отчёты': 'Отчёты руководителя',
  'прайсы': 'Прайсы и КП',
  'инженерия': 'Инженерия',
  'реестр': 'Реестры и справочники',
  'склад': 'Склад и отгрузка',
  'бухгалтерия': 'Бухгалтерия',
}

function имяГруппы(код: string): string {
  return ИМЯ_ГРУППЫ[код] ?? (код.charAt(0).toUpperCase() + код.slice(1))
}

/** Подпись происхождения набора — человеку, а не программисту. */
const ОТКУДА: Record<Набор['source'], string> = {
  code: 'из кода',
  file: 'настроен здесь',
  override: 'изменён здесь',
}

export default function AdminNabory() {
  const запрос = useНаборы()
  const д = запрос.data
  const [выбран, setВыбран] = useState('')       // код профиля в редакторе справа
  const [новый, setНовый] = useState(false)

  const текущий = (д?.sets ?? []).find((н) => н.code === выбран) ?? null

  return (
    <QueryState isLoading={запрос.isLoading} error={запрос.error}>
      <div className="notice" style={{ marginBottom: 16 }}>
        Набор только прячет разделы: он задаёт состав меню и стартовой страницы,
        а права на данные даёт роль. Раздел вне роли не появится, какой бы набор
        ни был назначен. Составы действуют со следующего запроса человека —
        перезапуск и повторный вход не нужны.
      </div>

      {(д?.unknown_sets?.length ?? 0) > 0 && (
        <div className="notice" style={{ marginBottom: 16 }}>
          <b>Внимание:</b> у {(д!.unknown_sets!.length === 1 ? 'учётки' : 'учёток')}{' '}
          {д!.unknown_sets!.map((у) => `${у.username} (набор «${у.set}»)`).join(', ')}{' '}
          стоит набор, которого не существует. Набора нет — человек видит всё,
          что даёт роль. Смените набор на вкладке «Учётки» или создайте набор
          с этим кодом.
        </div>
      )}

      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start',
                    flexWrap: 'wrap', marginBottom: 16 }}>
        <div className="card" style={{ flex: '0 1 300px', minWidth: 240 }}>
          <div className="page-head" style={{ marginBottom: 8 }}>
            <b>Профили</b>
            <span className="spacer" />
            <button onClick={() => { setНовый(true); setВыбран('') }}>новый набор</button>
          </div>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {(д?.sets ?? []).map((н) => (
              <li key={н.code} style={{ marginBottom: 4 }}>
                <button
                  style={{ width: '100%', textAlign: 'left',
                           fontWeight: выбран === н.code ? 700 : 400 }}
                  aria-pressed={выбран === н.code}
                  onClick={() => { setВыбран(н.code); setНовый(false) }}>
                  {н.title} <span className="muted">({н.code})</span>
                  <br />
                  <span className="hint">
                    {н.sections === null
                      ? 'видит всё по роли'
                      : `разделов: ${н.sections.length}`}
                    {' · '}{ОТКУДА[н.source]}
                    {' · '}{(н.users?.length ?? 0) === 0
                      ? 'учёток нет'
                      : `учёток: ${н.users!.length}`}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div style={{ flex: '1 1 420px', minWidth: 300 }}>
          {новый && д && (
            <Редактор ключ="новый" исходный={null} разделы={д.sections}
                      готово={() => setНовый(false)} />
          )}
          {!новый && текущий && д && (
            <Редактор ключ={текущий.code} исходный={текущий} разделы={д.sections}
                      готово={() => setВыбран('')} />
          )}
          {!новый && !текущий && (
            <div className="card hint">
              Выберите профиль слева, чтобы поправить его состав, — или создайте
              новый кнопкой «новый набор».
            </div>
          )}
        </div>
      </div>

      {д && <Отделы д={д} />}
    </QueryState>
  )
}

/**
 * Редактор состава: каталог всех разделов чекбоксами, сгруппированный по
 * группам реестра, и переключатель «видит всё (не сужать)» — так владелец
 * заводит профиль вроде «админ». Кодовый набор правится перекрытием (файл
 * поверх кода), у перекрытого есть «вернуть к заводскому», файловый можно
 * удалить — если он не назначен ни учёткам, ни отделам (иначе BFF откажет).
 */
function Редактор({ ключ, исходный, разделы, готово }: {
  ключ: string
  исходный: Набор | null
  разделы: РазделДляНабора[]
  готово: () => void
}) {
  const сохранение = useСохранениеНабора()
  const удаление = useУдалениеНабора()
  const [код, setКод] = useState(исходный?.code ?? '')
  const [название, setНазвание] = useState(исходный?.title ?? '')
  const [всё, setВсё] = useState(исходный ? исходный.sections === null : false)
  // У «видит всё» правка галочек начинается со всех отмеченных: сняв
  // переключатель, владелец сужает от полного состава, а не с нуля.
  const [выбор, setВыбор] = useState<Set<string>>(
    () => new Set(исходный?.sections ?? (исходный ? разделы.map((р) => р.code) : [])))
  const [ошибка, setОшибка] = useState('')

  // Группы — в порядке первого появления раздела (он же порядок rights.РАЗДЕЛЫ).
  const группы: { имя: string; свои: РазделДляНабора[] }[] = []
  for (const р of разделы) {
    const г = группы.find((g) => g.имя === р.group)
    if (г) г.свои.push(р)
    else группы.push({ имя: р.group, свои: [р] })
  }

  const переключить = (кодРаздела: string) => {
    const с = new Set(выбор)
    if (с.has(кодРаздела)) с.delete(кодРаздела)
    else с.add(кодРаздела)
    setВыбор(с)
  }

  const сохранить = () => {
    setОшибка('')
    const к = код.trim().toLowerCase()
    if (!к || /\s/.test(к)) {
      setОшибка('Код набора — одно слово в нижнем регистре, например «закупщик».')
      return
    }
    if (!название.trim()) {
      setОшибка('Название пустое.')
      return
    }
    if (!всё && выбор.size === 0) {
      setОшибка('Не выбран ни один раздел — такой набор спрятал бы человеку всё меню. '
        + 'Чтобы человек видел всё по роли, включите «видит всё».')
      return
    }
    сохранение.mutate(
      { code: к, title: название.trim(), sections: всё ? null : [...выбор] },
      { onSuccess: готово })
  }

  const занято = сохранение.isPending || удаление.isPending
  const учётки = исходный?.users ?? []

  return (
    <div className="card" data-editor={ключ}>
      <div className="page-head" style={{ marginBottom: 4 }}>
        <b>{исходный ? `Состав «${исходный.title}»` : 'Новый набор'}</b>
        <span className="spacer" />
        {исходный?.source === 'override' && (
          <button disabled={занято}
                  title="Снять правку: набор вернётся к составу, заданному в коде"
                  onClick={() => удаление.mutate({ code: исходный.code },
                                                 { onSuccess: готово })}>
            вернуть к заводскому
          </button>
        )}
        {исходный?.source === 'file' && (
          <button disabled={занято}
                  onClick={() => удаление.mutate({ code: исходный.code },
                                                 { onSuccess: готово })}>
            удалить
          </button>
        )}
      </div>
      <div className="hint" style={{ margin: '4px 0 8px' }}>
        Набор только прячет — права даёт роль. Кассы, овертаймов и других
        владельческих разделов здесь нет: они выдаются поимённо и набором не режутся.
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center',
                    marginBottom: 8 }}>
        {!исходный && (
          <label>Код{' '}
            <input value={код} onChange={(e) => setКод(e.target.value)}
                   placeholder="закупщик" style={{ width: 130 }} />
          </label>
        )}
        <label>Название{' '}
          <input value={название} onChange={(e) => setНазвание(e.target.value)} />
        </label>
        <label className="nowrap" style={{ fontWeight: 400 }}>
          <input type="checkbox" checked={всё} onChange={() => setВсё(!всё)} />{' '}
          видит всё (не сужать)
        </label>
        <span className="hint">
          {всё
            ? 'видно всё, что даёт роль'
            : `выбрано разделов: ${выбор.size} из ${разделы.length}`}
        </span>
      </div>
      {!всё && группы.map((г) => (
        <div key={г.имя} style={{ marginBottom: 8 }}>
          <div className="hint" style={{ marginBottom: 4 }}>{имяГруппы(г.имя)}</div>
          <div style={{ display: 'flex', gap: '4px 14px', flexWrap: 'wrap' }}>
            {г.свои.map((р) => (
              <label key={р.code} className="nowrap" style={{ fontWeight: 400 }}>
                <input type="checkbox" checked={выбор.has(р.code)}
                       onChange={() => переключить(р.code)} />{' '}
                {р.title}
              </label>
            ))}
          </div>
        </div>
      ))}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <button onClick={сохранить} disabled={занято}>сохранить</button>
        <button onClick={готово} disabled={занято}>отмена</button>
      </div>
      {исходный && (
        <div className="hint" style={{ marginTop: 8 }}>
          {учётки.length === 0
            ? 'Профиль не назначен ни одной учётке.'
            : `Учётки с этим профилем: ${учётки.join(', ')}.`}
          {' '}Назначается на вкладке «Учётки» (поле «Набор»).
        </div>
      )}
      {(ошибка || сохранение.error || удаление.error) && (
        <div className="error" style={{ marginTop: 8 }}>
          {ошибка
            || (сохранение.error && (сохранение.error as Error).message)
            || (удаление.error as Error).message}
        </div>
      )}
    </div>
  )
}

/**
 * Привязка «отдел → набор»: какой набор достаётся учёткам этого отдела при
 * массовой заготовке (mkusers_bulk). На уже выданные учётки привязка задним
 * числом не действует — их набор меняется на вкладке «Учётки» по одному.
 */
function Отделы({ д }: { д: NonNullable<ReturnType<typeof useНаборы>['data']> }) {
  const привязка = useПривязкаОтдела()
  const отделы = д.depts ?? []
  // Отделы, которых ядро не назвало, но привязка на них есть, — тоже показать:
  // молча спрятать настройку хуже, чем показать её голым номером.
  const известные = new Set(отделы.map((о) => о.id))
  const осиротевшие = Object.keys(д.dept_sets)
    .map(Number)
    .filter((id) => Number.isInteger(id) && !известные.has(id))
    .map((id) => ({ id, name: `отдел #${id}` }))
  const все = [...отделы, ...осиротевшие]

  return (
    <div className="card">
      <div className="page-head" style={{ marginBottom: 8 }}>
        <b>Отдел → набор</b>
        <span className="spacer" />
        <span className="hint">привязано: {Object.keys(д.dept_sets).length}</span>
      </div>
      <div className="hint" style={{ marginBottom: 8 }}>
        Привязка действует при заведении учёток из заготовки: людям отдела
        достанется этот набор. «— по роли» — набора нет, видно всё, что даёт роль.
      </div>
      {привязка.error && (
        <div className="error" style={{ marginBottom: 8 }}>
          {(привязка.error as Error).message}
        </div>
      )}
      {все.length === 0 ? (
        <div className="hint">
          Ядро не отдало структуру отделов (/refs), а привязок в файле нет.
        </div>
      ) : (
        <div className="table-wrap cards">
          <table>
            <thead>
              <tr>
                <th>Отдел</th>
                <th className="nowrap">Набор</th>
              </tr>
            </thead>
            <tbody>
              {все.map((о) => (
                <tr key={о.id}>
                  <td data-label="Отдел">{о.name} <span className="muted">#{о.id}</span></td>
                  <td data-label="Набор" className="nowrap">
                    <select value={д.dept_sets[String(о.id)] ?? ''}
                            disabled={привязка.isPending}
                            onChange={(e) => привязка.mutate({ dept_id: о.id,
                                                               set: e.target.value })}>
                      <option value="">— по роли</option>
                      {д.sets.map((н) => (
                        <option key={н.code} value={н.code}>{н.title}</option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
